import { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { Controller } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

// * cards
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import CreditCardInput from "react-credit-card-input";
import { FormHelperText } from "@material-ui/core";
import SharedModal from "./shared/SharedModal";
import { CardWarningModal } from "./CardWarningModal";
import { getDocumentTypes } from "services/payments";
import { useAuthToken } from "hooks/useAuthToken";

const Main = styled(Grid)`
  .userCard {
    width: 120px;
    height: 75px;
    background-color: #999999;
    box-shadow: 0px 5px 15px rgba(145, 158, 171, 0.15);
    border-radius: 7px;
    position: relative;
    transition: all 0.4s ease;
    font-size: 10px;
  }

  .userCard:hover {
    background-color: #b7b7b7;
  }

  .cardNumber {
    position: absolute;
    bottom: 25px;
    color: #ffffff;
  }

  .cardName {
    position: absolute;
    bottom: 10px;
    color: #ffffff;
  }

  .cardChip {
    position: absolute;
    width: 12px;
    height: 10px;
    background: #f7d479;
    border-radius: 2px;
    top: 10px;
    left: 10px;
  }
`;

const SelectableItem = styled(Box)`
  background-color: ${(props) => (props.isSelected ? "#E5005105" : "none")};
  border: 1px solid ${(props) => (props.isSelected ? "#E50051" : "#282828")};
  padding: 10px;
  position: relative;

  .selectedText {
    position: absolute;
    bottom: 10px;
    right: 10px;
  }
`;

export default function CardList({
  data,
  cvc,
  setCvc,
  cardNumber,
  setCardNumber,
  setCardName,
  expiry,
  setExpiry,
  selectedCard,
  setSelectedCard,
  control,
  reset,
  pushError = () => null,
  errors,
}) {
  const [focus, setFocus] = useState("");
  const [addCard, setAddCard] = useState(true);
  const [displayedCardName, setDisplayedCardName] = useState("");
  const [docTypes, setDocTypes] = useState([]);
  const [currentDocType, setCurrentDocType] = useState(10); // 10 -> C.C
  // const [showWarningModal, setShowWarningModal] = useState(true);

  const { getAuthToken } = useAuthToken();

  const handleChangeCard = (e) => {
    setFocus(e);
  };

  useEffect(() => {
    setSelectedCard(false);
    reset();
  }, [addCard]);

  useEffect(() => {
    (async () => {
      await getAuthToken();
      const { data } = await getDocumentTypes();
      if (data?.status === "success") {
        setDocTypes(data?.data);
      }
    })();
  }, []);

  const regexDocNumber = useMemo(() => {
    const onlyNumber = [10, 9, 20, 50];

    if (onlyNumber.includes(currentDocType)) {
      return /^[1-9][0-9]{0,10}$/;
    }

    return /^[a-zA-Z0-9_]{0,10}$/;
  }, [currentDocType]);

  return (
    <Main
      container
      style={{ maxWidth: 400, display: "flex", margin: "auto", paddingLeft: 0 }}
      spacing={2}
    >
      {/* {data.cards && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                color="secondary"
                variant={!addCard ? "outlined" : "contained"}
                onClick={() => setAddCard(true)}
              >
                {"Nueva tarjeta"}
              </Button>
            </Grid>

            <Grid item xs={6}>
              <Button
                fullWidth
                color="secondary"
                variant={addCard ? "outlined" : "contained"}
                onClick={() => setAddCard(false)}
              >
                {"Mis Tarjetas"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )} */}
      {addCard ? (
        <Grid item xs={12} style={{ paddingRight: 16 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ paddingLeft: 0, paddingTop: 0 }}>
              <Box>
                <Cards
                  cvc={cvc}
                  expiry={expiry}
                  focused={focus}
                  name={displayedCardName}
                  number={cardNumber}
                  placeholders={{ name: "NOMBRE" }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} style={{ paddingLeft: 0 }}>
              <Box display={"flex"} justifyContent={"center"} mb={1}>
                <CreditCardInput
                  cardCVCInputProps={{
                    onFocus: (e) => handleChangeCard("cvc"),
                    onChange: (e) => {
                      pushError("cardCVC");
                      setCvc(e.target.value);
                    },
                  }}
                  cardExpiryInputProps={{
                    onFocus: (e) => handleChangeCard("expiry"),
                    onChange: (e) => {
                      pushError("cardExpiry");
                      setExpiry(e.target.value.replace(/\s/g, ""));
                    },
                  }}
                  cardNumberInputProps={{
                    onFocus: (e) => handleChangeCard("number"),
                    onChange: (e) => {
                      pushError("cardNumber");
                      setCardNumber(e.target.value);
                    },
                  }}
                  customTextLabels={{
                    invalidCardNumber: "El número de la tarjeta es inválido",
                    expiryError: {
                      invalidExpiryDate: "La fecha de expiración es inválida",
                      monthOutOfRange:
                        "El mes de expiración debe estar entre 01 y 12",
                      yearOutOfRange:
                        "El año de expiración no puede estar en el pasado",
                      dateOutOfRange:
                        "La fecha de expiración no puede estar en el pasado",
                    },
                    invalidCvc: "El código de seguridad es inválido",
                    invalidZipCode: "El código postal es inválido",
                    cardNumberPlaceholder: "NÚMERO DE TARJETA",
                    expiryPlaceholder: "MM/AA",
                    cvcPlaceholder: "CVC",
                    zipPlaceholder: "C.P.",
                  }}
                  fieldStyle={{
                    width: "100%",
                    borderBottom: "1px solid #888",
                  }}
                  inputStyle={{
                    width: "100%",
                    fontFamily: "Gotham Medium",
                    fontSize: 13,
                  }}
                  onError={(err) => {
                    pushError(err.inputName, true);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4} style={{ paddingLeft: 0 }}>
              <Controller
                rules={{ required: true }}
                control={control}
                name={"type_document"}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel style={{ fontSize: 13 }}>
                      {"TIPO DE DOCUMENTO"}
                    </InputLabel>
                    <Select
                      {...field}
                      variant="standard"
                      fullWidth
                      style={{ fontFamily: "Gotham Medium", fontSize: 13 }}
                      placeholder="TIPO DE DOCUMENTO"
                      error={errors?.type_document}
                      onChange={(e) => {
                        setCurrentDocType(e.target.value);
                        field.onChange(e.target.value);
                      }}
                    >
                      {docTypes.map((type) => (
                        <MenuItem
                          style={{ fontFamily: "Gotham Medium", fontSize: 13 }}
                          key={type?.id}
                          value={type?.id}
                        >
                          {type?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              {errors?.type_document && (
                <FormHelperText error>
                  {"Este campo es requerido*"}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={8}>
              <Controller
                rules={{
                  required: {
                    value: true,
                    message: "Este campo es requerido*",
                  },
                  minLength: {
                    value: 6,
                    message: "Minimo 6 digitos y/o carácter",
                  },
                  pattern: {
                    value: regexDocNumber,
                    message: "El formato no es válido",
                  },
                }}
                control={control}
                name={"number_document"}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    inputProps={{
                      style: { fontFamily: "Gotham Medium", fontSize: 13 },
                    }}
                    InputLabelProps={{ style: { fontSize: "13px" } }}
                    type="text"
                    variant="standard"
                    error={errors?.number_document}
                    placeholder={"NÚMERO DE DOCUMENTO"}
                    label={"NÚMERO DE DOCUMENTO"}
                    onChange={(e) => {
                      field.onChange(e.target.value?.toUpperCase());
                    }}
                  />
                )}
              />
              {errors?.number_document && (
                <FormHelperText error>
                  {errors.number_document?.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} style={{ paddingLeft: 0 }}>
              <Controller
                rules={{ required: true }}
                control={control}
                name={"name"}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant={"standard"}
                    placeholder={"NOMBRE"}
                    label={"NOMBRE"}
                    fullWidth
                    inputProps={{
                      style: { fontFamily: "Gotham Medium", fontSize: 13 },
                    }}
                    InputLabelProps={{ style: { fontSize: "13px" } }}
                    error={errors?.name}
                    onChange={(e) => {
                      const name = e.target.value?.toUpperCase();
                      field.onChange(name);
                      setCardName(name);
                      setDisplayedCardName(name);
                    }}
                    onFocus={(e) => handleChangeCard("name")}
                  />
                )}
              />
              {errors?.name && (
                <FormHelperText error>
                  {"Este campo es requerido*"}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={12}>
          {data?.cards?.map((item, index) => (
            <SelectableItem
              isSelected={
                selectedCard.payment_method_id == item.payment_method_id
              }
              key={`card-${index}`}
              onClick={() => setSelectedCard(item)}
              display={"flex"}
              alignItems={"center"}
            >
              <Box className="userCard" p={2}>
                <Box className="cardChip" />
                <Typography className="cardNumber" fontSize={"10px"}>
                  **** **** **** {item.last_four_number}
                </Typography>
                <Typography className="cardName" fontSize={"10px"}>
                  {item.name_credit_card}
                </Typography>
              </Box>
              <Box display={"flex"} flexDirection={"column"} ml={2}>
                <Typography>{item.name_credit_card}</Typography>
                <Typography variant="body2">
                  **** **** **** {item.last_four_number}
                </Typography>
                {selectedCard.payment_method_id == item.payment_method_id && (
                  <Box className={"selectedText"}>
                    <Typography variant="body2" color="primary">
                      {"Seleccionada"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </SelectableItem>
          ))}
        </Grid>
      )}
      {/* <SharedModal
        isOpen={showWarningModal}
        setIsOpen={setShowWarningModal}
        modalBody={
          <CardWarningModal setShowWarningModal={setShowWarningModal} />
        }
      /> */}
    </Main>
  );
}
